import { Container, Grid, Typography } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { AsolviSwitch } from "components/AsolviSwitch";
import FullScreenDialog from "components/FullScreenDialog";
import PrimaryButton from "components/PrimaryButton";
import { ConsentObject, getBasicConsent, isAbortError } from "helpers";
import { UserConsentType, UserConsentTypeEnum } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { updatePartnerBasicConsent } from "store/slices/partner.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { updateUserConsent } from "store/slices/user.store";

interface UserConsentProps {
  open: boolean;
  partnerView?: boolean;
  setOpen: (open: boolean) => void;
}

export const BasicUserConsentDialog: FC<UserConsentProps> = (props) => {
  const { open, setOpen, partnerView } = props;
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { languageVar } = useAppSelector((state) => state.user);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentObject, setConsentObject] = useState<ConsentObject>();

  useEffect(() => {
    getBasicConsent(languageVar).then((c) => setConsentObject(c));
  }, [languageVar]);

  const addUserConsent = useCallback(
    async (accepted: boolean = false) => {
      let consent: UserConsentType = {
        accepted: accepted,
        version: consentObject!.version,
        type: UserConsentTypeEnum.Basic,
      };
      if (partnerView) {
        dispatch(updatePartnerBasicConsent({ consent }));
        setOpen(false);
        if (consentObject!.required && !consent.accepted) {
          navigate("/login", { replace: true });
        }
      } else {
        dispatch(updateUserConsent({ consent }))
          .then(unwrapResult)
          .then(() => setOpen(false))
          .catch((e) => {
            if (isAbortError(e)) return;
            dispatch(addSnackbarMessage({ key: "AddUserConsent-fail" }));
          });
      }
    },
    [consentObject, dispatch, navigate, partnerView, setOpen]
  );

  if (!consentObject) {
    return null;
  }
  return (
    <FullScreenDialog
      open={open}
      setOpen={setOpen}
      title={intl.formatMessage({ id: "userConsent.dialogTitle" })}
      onClose={() => {
        addUserConsent();
        if (consentObject.required) {
          if (partnerView) {
            navigate("/login", { replace: true });
          } else {
            navigate("/logout", { replace: true });
          }
        }
      }}
      child={
        <Container data-testid="BasicUserConsentDialog">
          <Grid container alignItems="center" justifyContent="center">
            <Typography
              component="div"
              whiteSpace="pre-line"
              sx={{ paddingBottom: "20px", paddingTop: "20px" }}
            >
              <ReactMarkdown className="markdown-text" children={consentObject.text} />
            </Typography>
          </Grid>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="body2">
                <FormattedMessage id="userConsent.toggleText" />
              </Typography>
            </Grid>
            <Grid item>
              <AsolviSwitch
                key="basicConsent"
                checked={consentAccepted}
                onChange={() => {
                  setConsentAccepted(!consentAccepted);
                }}
              />
            </Grid>
          </Grid>
          <Grid container alignItems="center" justifyContent="center" sx={{ paddingTop: "20px" }}>
            <Grid item alignItems="center" xs={12}>
              <PrimaryButton
                type="submit"
                variant="contained"
                disabled={!consentAccepted && consentObject.required}
                fullWidth
                onClick={() => {
                  addUserConsent(consentAccepted);
                }}
              >
                <FormattedMessage id="trackingConsent.saveAndContinue" />
              </PrimaryButton>
            </Grid>
          </Grid>
        </Container>
      }
    />
  );
};
